/**
 * Reads a pack size exactly as typed off an invoice ("4/5 LB BG", "6/#10 CAN")
 * into a count, a per-unit size, a `uoms.code`, and (when recognised) the
 * supplier's container name. Ported from the "UoM & pack size" prototype's
 * `parse()` (plans/uom/project/IngredientUoM.dc.html), but the invoice-alias →
 * uom-code map and the container abbreviation → name map are no longer
 * hardcoded: both come from `GET /units/uoms` and `GET /units/pack-containers`
 * (see `uom-reference.api.ts`) so a reference-table change here needs no
 * client redeploy.
 *
 * Sizes/units are per case, not yet multiplied into base units — the caller
 * (the UoM & pack size tab) does that with `uom.toBase` once a uom is chosen.
 */

export interface PackUomOption {
  code: string;
  label: string;
  type: "WEIGHT" | "LIQUID" | "EACH";
  toBase: number;
  system: string | null;
  isPack: boolean;
  isRecipe: boolean;
  isDensityRef: boolean;
  /** Invoice spellings ("LBS", "#"), upper-case. */
  aliases: string[];
  sortOrder: number;
}

export interface PackContainerOption {
  /** Invoice abbreviation ("BG", "#10"). */
  abbr: string;
  /** Friendly name ("bag", "#10 can"). */
  name: string;
}

export type PackSizeParseStatus = "ok" | "ambiguous" | "error" | "empty";

export interface PackSizeParseResult {
  status: PackSizeParseStatus;
  count: number | null;
  size: number | null;
  /** `uoms.code`. Null until read (`error`/`empty`) or resolved (`ambiguous`). */
  uomCode: string | null;
  /** Friendly container name, or the raw trailing word when unrecognised. Null when there was none. */
  container: string | null;
  /** "1/40# AVG" — a nominal size the supplier doesn't guarantee exactly. */
  catchWeight: boolean;
}

const EMPTY_RESULT: PackSizeParseResult = {
  status: "empty",
  count: null,
  size: null,
  uomCode: null,
  container: null,
  catchWeight: false,
};

/** The literal token that is ambiguous between weight ounces and fluid ounces. */
const AMBIGUOUS_OZ_TOKEN = "OZ";

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/** Longest alias first, so "FL OZ"/"LBS" win over "OZ"/"LB" in the alternation. */
function sortedAliasEntries(uoms: PackUomOption[]): { alias: string; code: string }[] {
  const entries = uoms.flatMap((uom) =>
    uom.aliases.map((alias) => ({ alias: alias.toUpperCase().trim(), code: uom.code })),
  );
  return entries
    .filter((entry) => entry.alias.length > 0)
    .sort((a, b) => b.alias.length - a.alias.length);
}

function findContainer(word: string, containers: PackContainerOption[]): string {
  const match = containers.find((c) => c.abbr.toUpperCase() === word);
  return match ? match.name : word.toLowerCase();
}

/**
 * Splits the text after the unit into container words, pulling out a
 * standalone "AVG" ("1/40# AVG") as the catch-weight flag rather than a
 * container name.
 */
function readContainer(
  rest: string,
  containers: PackContainerOption[],
): { container: string | null; catchWeight: boolean } {
  const words = rest.trim().split(/\s+/).filter(Boolean);
  const catchWeight = words.some((w) => w.toUpperCase() === "AVG");
  const remaining = words.filter((w) => w.toUpperCase() !== "AVG");
  if (remaining.length === 0) return { container: null, catchWeight };
  return { container: findContainer(remaining[0] ?? "", containers), catchWeight };
}

/**
 * Parses one pack-size string. `disambiguatedUomCode` resolves a bare "OZ"
 * token to whichever `uoms.code` the caller picked (weight `oz` or liquid
 * `floz`, per the "which is this?" prompt); omit it while that choice is
 * still pending and the ambiguous token comes back as `status: 'ambiguous'`.
 */
export function parsePackSize(
  input: string,
  uoms: PackUomOption[],
  containers: PackContainerOption[],
  disambiguatedUomCode?: string | null,
): PackSizeParseResult {
  const text = String(input ?? "")
    .trim()
    .toUpperCase()
    .replace(/\s+/g, " ");
  if (!text) return EMPTY_RESULT;

  // "6/#10 CAN" — a #10 can is a fixed food-service convention (~106 fl oz),
  // not something derivable from the reference tables, so its size is
  // hardcoded; the container name itself still comes from `containers`.
  const tenCanMatch = text.match(/^(\d+)\s*\/\s*#10\s*([A-Z]*)$/);
  if (tenCanMatch) {
    const flozUom = uoms.find((u) => u.code === "floz");
    const tenCan = containers.find((c) => c.abbr === "#10");
    return {
      status: "ok",
      count: Number(tenCanMatch[1]),
      size: 106,
      uomCode: flozUom?.code ?? null,
      container: tenCan?.name ?? "#10 can",
      catchWeight: false,
    };
  }

  const aliasAlternation = sortedAliasEntries(uoms)
    .map((e) => escapeRegExp(e.alias))
    .join("|");
  // The "count /" is optional: "50 LB BAG" is one 50 lb bag. A unit spelled
  // with letters has to end there, so the "C" (cup) in "6 CAN" is not a unit.
  const mainRegex = new RegExp(
    `^(?:(\\d+)\\s*\\/\\s*)?(\\d*\\.?\\d+)\\s*(${aliasAlternation})(?:(?<=#)|(?![A-Z]))\\s*([A-Z ]*)$`,
  );
  const main = aliasAlternation ? text.match(mainRegex) : null;

  if (!main) {
    // Couldn't read a "count / size unit" shape at all — best-effort count and
    // container so the manual editor isn't starting from nothing.
    const fallback = text.match(/^(\d+)\s*(?:CT\s*)?([A-Z]+)/);
    if (!fallback) {
      return { status: "error", count: null, size: null, uomCode: null, container: null, catchWeight: false };
    }
    const { container, catchWeight } = readContainer(fallback[2] ?? "", containers);
    return {
      status: "error",
      count: Number(fallback[1]),
      size: null,
      uomCode: null,
      container,
      catchWeight,
    };
  }

  const count = main[1] ? Number(main[1]) : 1;
  const size = Number(main[2]);
  const matchedAlias = (main[3] ?? "").replace(/\s+/g, " ").trim();
  const { container, catchWeight } = readContainer(main[4] ?? "", containers);

  if (matchedAlias === AMBIGUOUS_OZ_TOKEN) {
    if (!disambiguatedUomCode) {
      return { status: "ambiguous", count, size, uomCode: null, container, catchWeight };
    }
    return { status: "ok", count, size, uomCode: disambiguatedUomCode, container, catchWeight };
  }

  const uom = sortedAliasEntries(uoms).find((e) => e.alias === matchedAlias);
  return { status: "ok", count, size, uomCode: uom?.code ?? null, container, catchWeight };
}
